import React from "react";
import { Modal, Button, Table } from "react-bootstrap";

const ViewCollectionRequestModal = ({ show, handleClose, collectionRequest }) => {
  if (!collectionRequest) return null;

  // Destructure the collection request object.
  const {
    type,
    amount,
    method,
    nameOnCheque,
    transactionNumber,
    status,
    reason,
    "inOrOut": inOut,
    created_at,
    updated_at,
    broker,
    cil,
  } = collectionRequest;

  // Define keys to exclude for broker details (removed iban, nameOfAccount, bankName)
  const brokerExcludedKeys = [
    "type",
    "status",
    "businessCard",
    "swiftCode",
    "branchNumber",
    "bankAddress",
    "picture",
    "address",
    "fcm",
    "developer_id",
    "autoApprove",
    "isBlocked",
    "created_at",
    "updated_at"
  ];

  // Friendly labels dictionary to display better column names
  const friendlyLabels = {
    // Collection Request Fields
    type: "Collection Type",
    amount: "Amount",
    method: "Method",
    nameOnCheque:"Name On Cheque",
    transactionNumber: "Transaction Number",
    status: "Status",
    reason: "Reason",
    inOut: "Direction",
    created_at: "Created At",
    updated_at: "Updated At",
    // Broker Details Fields
    name: "Name",
    email: "Email",
    mobile: "Mobile Number",
    mobileOfReference: "Reference Mobile",
    iban: "IBAN",
    nameOfAccount: "Account Name",
    bankName: "Bank Name",
    // CIL Details Fields
    clientName: "Client Name",
    clientNumber: "Client Number",
    projectName: "Project Name",
    developerName: "Developer Name",
    developerSalesNumber: "Developer Sales Number",
    unitCode: "Unit Code",
    typeOfUnit: "Type of Unit",
    dealValue: "Deal Value",
    actualAmount: "Actual Amount",
    document: "Document",
    expirationDate: "Expiration Date",
    // Add more mappings as needed...
  };

  /**
   * Renders table rows for a given data object.
   * @param {Object} dataObj - The data object to display.
   * @param {Array} excludedKeys - Optional array of keys to exclude from rendering.
   */
  const renderTableRows = (dataObj, excludedKeys = []) => {
    return Object.entries(dataObj).map(([key, value]) => {
      // Exclude any keys that include "id" (e.g. "id", "broker_id", etc.)
      if (key.toLowerCase().includes("id")) return null;

      // Exclude keys if they are in the provided excludedKeys array.
      if (excludedKeys.includes(key)) return null;

      // Format dates for "created_at" and "updated_at"
      if (key === "created_at" || key === "updated_at") {
        value = new Date(value).toLocaleString();
      }

      // If the value is a URL (e.g. an image), render it as an image.
      if (typeof value === "string" && value.startsWith("http")) {
        return (
          <tr key={key}>
            <td style={{ width: "30%", fontWeight: "bold" }}>
              {friendlyLabels[key] || key}
            </td>
            <td>
              <img src={value} alt={key} style={{ maxWidth: "150px" }} />
            </td>
          </tr>
        );
      }

      return (
        <tr key={key}>
          <td style={{ width: "30%", fontWeight: "bold" }}>
            {friendlyLabels[key] || key}
          </td>
          <td>{value !== null ? value.toString() : "-"}</td>
        </tr>
      );
    });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Collection Request Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Collection Request Section */}
        <h5 className="mb-3">Collection Request</h5>
        <Table striped bordered hover responsive>
          <tbody>
            {renderTableRows({
              type,
              amount,
              method,
              nameOnCheque,
              transactionNumber,
              status,
              reason,
              inOut,
              created_at,
              updated_at,
            })}
          </tbody>
        </Table>

        {/* Broker Details Section */}
        {broker && (
          <>
            <h5 className="mt-4 mb-3">Broker Details</h5>
            <Table striped bordered hover responsive>
              <tbody>{renderTableRows(broker, brokerExcludedKeys)}</tbody>
            </Table>
          </>
        )}

        {/* CIL Details Section */}
        {cil && (
          <>
            <h5 className="mt-4 mb-3">CIL Details</h5>
            <Table striped bordered hover responsive>
              <tbody>{renderTableRows(cil)}</tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewCollectionRequestModal;
